import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { examLibraryApi, batchApi, studentApi, apiErrorMessage } from '../services/api';
import { useOrg, useReloadOrg } from '../context/OrgContext';
import { useToast } from '../context/ToastContext';
import { Button, Card, Field, Spinner } from '../components/ui';
import JoinCodeCard from '../components/JoinCodeCard';

// Three screens that take a brand-new institute from an empty console to a
// state where a student can be assigned a test. Every call it makes is one
// the regular pages already make; this just puts them in order.

const STEPS = [
  { key: 'exam', label: 'Choose an exam' },
  { key: 'batch', label: 'Create your first batch' },
  { key: 'students', label: 'Bring in students' },
];

const getExamId = (exam) => exam.examId || exam.id;

export default function WelcomePage() {
  const org = useOrg();
  const reloadOrg = useReloadOrg();
  const toast = useToast();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [catalogue, setCatalogue] = useState([]);
  const [enrolled, setEnrolled] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [selectedExamId, setSelectedExamId] = useState('');
  const [batchName, setBatchName] = useState('');
  const [createdBatch, setCreatedBatch] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [invited, setInvited] = useState([]);

  useEffect(() => {
    Promise.all([examLibraryApi.catalogue(org.id), examLibraryApi.enrolled(org.id)])
      .then(([catRes, enrRes]) => {
        // A retired exam is no place to start a new institute.
        const cat = (catRes.data?.exams || catRes.data || []).filter((e) => e.isActive !== false);
        const enr = enrRes.data || [];
        setCatalogue(cat);
        setEnrolled(enr);
        if (enr.length) setSelectedExamId(getExamId(enr[0]));
      })
      .catch((err) => toast.error(apiErrorMessage(err, 'Could not load the exam catalogue')))
      .finally(() => setLoading(false));
  }, [org.id, toast]);

  const enrolledIds = useMemo(() => new Set(enrolled.map(getExamId)), [enrolled]);
  const selectedExam = useMemo(
    () => catalogue.find((e) => getExamId(e) === selectedExamId) || enrolled.find((e) => getExamId(e) === selectedExamId),
    [catalogue, enrolled, selectedExamId]
  );

  const finishExamStep = async () => {
    if (!selectedExamId) return;
    if (enrolledIds.has(selectedExamId)) { setStep(1); return; }
    setBusy(true);
    try {
      await examLibraryApi.enroll(org.id, selectedExamId);
      setEnrolled((items) => [...items, { examId: selectedExamId, title: selectedExam?.title }]);
      toast.success(`${selectedExam?.title || 'Exam'} added to your library.`);
      setStep(1);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not enrol in that exam'));
    } finally {
      setBusy(false);
    }
  };

  const finishBatchStep = async () => {
    if (createdBatch) { setStep(2); return; }
    if (!batchName.trim()) return;
    setBusy(true);
    try {
      const res = await batchApi.create(org.id, { name: batchName.trim(), examId: selectedExamId });
      setCreatedBatch(res.data);
      toast.success(`${batchName.trim()} created.`);
      setStep(2);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not create the batch'));
    } finally {
      setBusy(false);
    }
  };

  const invite = async () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email) return;
    setBusy(true);
    try {
      await studentApi.invite(org.id, { email, batchId: createdBatch?.id });
      setInvited((items) => [...items, email]);
      setInviteEmail('');
      toast.success(`Invitation sent to ${email}.`);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not invite that student'));
    } finally {
      setBusy(false);
    }
  };

  const finish = async () => {
    await reloadOrg();
    navigate('/', { replace: true });
  };

  if (loading) return <div className="page-loading"><Spinner /> Preparing your setup…</div>;

  return (
    <div className="welcome-shell">
      <div className="welcome-steps">
        {STEPS.map((s, i) => (
          <div key={s.key} className={`welcome-step ${i === step ? 'active' : i < step ? 'done' : ''}`}>
            <span className="checklist-marker">{i < step ? '✓' : i + 1}</span>
            {s.label}
          </div>
        ))}
      </div>

      <Card className="welcome-body">
        {step === 0 && (
          <>
            <h2>Which exam are your students preparing for?</h2>
            <p className="muted">
              Everything in your console is built on this: question banks, notes, batches and tests. You can add more exams later from the Exam library.
            </p>
            {catalogue.length ? (
              <div className="exam-pick">
                {catalogue.map((exam) => {
                  const id = getExamId(exam);
                  return (
                    <button
                      key={id}
                      type="button"
                      className={selectedExamId === id ? 'selected' : ''}
                      onClick={() => setSelectedExamId(id)}
                    >
                      <b>{exam.title}</b>
                      <small>{enrolledIds.has(id) ? 'Already in your library' : exam.description || 'Prepgen.ai managed exam'}</small>
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="muted">The catalogue is empty right now. You can skip this and enrol later.</p>
            )}
            <div className="welcome-actions">
              <Button variant="text" onClick={finish}>Skip setup</Button>
              <Button disabled={!selectedExamId || busy} onClick={finishExamStep}>
                {busy ? 'Adding…' : 'Continue'}
              </Button>
            </div>
          </>
        )}

        {step === 1 && (
          <>
            <h2>Name your first batch</h2>
            <p className="muted">
              A batch is a group of students preparing for the same exam. Tests and notes are assigned to a batch, so every student needs to be in one.
            </p>
            <Field label="Batch name">
              <input
                autoFocus
                placeholder={`e.g. ${selectedExam?.title || 'CAT'} 2026 Morning`}
                value={createdBatch?.name || batchName}
                disabled={Boolean(createdBatch)}
                onChange={(e) => setBatchName(e.target.value)}
              />
            </Field>
            <p className="muted" style={{ fontSize: 12 }}>Preparing for <b>{selectedExam?.title || 'the selected exam'}</b>.</p>
            <div className="welcome-actions">
              <Button variant="text" onClick={() => setStep(0)}>Back</Button>
              <Button disabled={(!createdBatch && !batchName.trim()) || busy} onClick={finishBatchStep}>
                {busy ? 'Creating…' : createdBatch ? 'Continue' : 'Create batch'}
              </Button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <h2>Bring in your students</h2>
            <p className="muted">
              Two ways, and you can use both. Share the join code with a whole class, or invite individuals by email. Students need a Prepgen account; with the code they create one themselves.
            </p>
            <JoinCodeCard code={org.code} />
            <div style={{ height: 16 }} />
            <Field label="Invite by email (optional)">
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="email"
                  placeholder="student@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); invite(); } }}
                />
                <Button variant="secondary" disabled={!inviteEmail.trim() || busy} onClick={invite}>Invite</Button>
              </div>
            </Field>
            {invited.length > 0 && (
              <p className="muted" style={{ fontSize: 12 }}>
                Invited: {invited.join(', ')}. They will be placed in <b>{createdBatch?.name}</b> when they accept.
              </p>
            )}
            <div className="welcome-actions">
              <Button variant="text" onClick={() => setStep(1)}>Back</Button>
              <Button onClick={finish}>Go to my dashboard</Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
